import * as React from 'react'
import styled from 'styled-components'
import * as constants from '../../static/constants'
import * as interfaces from '../../static/interfaces'
import AvatarImage from '../atoms/AvatarImage'
import Score from '../atoms/Score'
import TextWrap from '../atoms/TextWrap'
import Typography from '../atoms/_layouts/Typography'
import SvgIcon from 'components/atoms/SvgIcon'
import { LightgalleryProvider, LightgalleryItem, useLightgallery } from 'react-lightgallery'
import { Swiper, SwiperSlide } from 'swiper/react'
import SwiperCore, { FreeMode, Navigation, Thumbs } from 'swiper'

SwiperCore.use([FreeMode, Navigation, Thumbs])

interface IProps {
  review: interfaces.IReview
  showReply?: boolean
  listView?: boolean
  singleStretch?: boolean
  showImage?: boolean
}

interface IReviewAvatarProps {
  review: interfaces.IReview
  reply?: boolean
}

interface IReviewImagesProps {
  imageUrls: string[]
  reviewId: string | number
}

const ReviewAvatar: React.FC<IReviewAvatarProps> = ({ review, reply }) => {
  const reviewerName = React.useMemo(() => {
    if (reply) {
      return review.review_reply.user.username
    }
    return (
      review.reviewer?.username ||
      review.anonymous_review_author ||
      review.direct_reservation?.guest_name ||
      ''
    )
  }, [review])

  const reviewerAvatarURL = React.useMemo(() => {
    if (reply) {
      return review.review_reply.user.avatar_url
    }
    if (review.reviewer) {
      return review.reviewer.avatar_url
    }

    return `${constants.ASSET_HOST}/images/no-avatar.svg`
  }, [review, reply])

  return (
    <Avatar className="Avatar">
      <div className="Avatar_Image">
        <AvatarImage avatarURL={reviewerAvatarURL} />
      </div>
      <div className="Avatar_Info">
        <h4>{reviewerName}</h4>
      </div>
    </Avatar>
  )
}

function ReviewImageSwiper({ imageUrls, reviewId }: IReviewImagesProps) {
  const { openGallery } = useLightgallery()

  const handleImageError = React.useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    e.currentTarget.style.display = 'none'
  }, [])

  const open = React.useCallback(
    (index: number) => {
      openGallery(`reviewImages-${reviewId}`, index)
    },
    [imageUrls]
  )

  return (
    <div className="reviewImages">
      <Swiper
        navigation={{
          nextEl: `.button-next-${reviewId}`,
          prevEl: `.button-prev-${reviewId}`,
          disabledClass: 'button-disabled',
        }}
        slidesPerView={'auto'}
        slidesPerGroup={1}
        spaceBetween={8}
        className="reviewSwiper"
        breakpoints={{
          600: {
            spaceBetween: 12,
          },
        }}
      >
        {imageUrls.map((url, index) => (
          <SwiperSlide key={index}>
            <ImageContainer onClick={() => open(index)}>
              <img
                src={url}
                alt={`Review image ${index + 1}`}
                loading="lazy"
                onError={handleImageError}
              />
            </ImageContainer>
          </SwiperSlide>
        ))}

        {imageUrls.length > 1 && (
          <>
            <button
              type="button"
              className={`button-prev button-prev-${reviewId}`}
              aria-label="Previous Image"
            >
              <SvgIcon
                name="keyboard_arrow_left"
                className="icon"
                ariaHidden={true}
                role="presentation"
              />
            </button>
            <button
              type="button"
              className={`button-next button-next-${reviewId}`}
              aria-label="Next Image"
            >
              <SvgIcon
                name="keyboard_arrow_right"
                className="icon"
                ariaHidden={true}
                role="presentation"
              />
            </button>
          </>
        )}
      </Swiper>
    </div>
  )
}

const ReviewImages: React.FC<IReviewImagesProps> = ({ imageUrls, reviewId }) => {
  if (!imageUrls || imageUrls.length === 0) {
    return null
  }

  return (
    <ImageGalleryWrapper className="Review_Images">
      <LightgalleryProvider
        plugins={['lg-fullscreen.js', 'lg-zoom.js']}
        lightgallerySettings={{
          zoom: false,
          fullScreen: false,
          download: false,
        }}
      >
        {imageUrls.map((url, index) => (
          <LightgalleryItem key={index} group={`reviewImages-${reviewId}`} src={url} />
        ))}
        <ReviewImageSwiper imageUrls={imageUrls} reviewId={reviewId} />
      </LightgalleryProvider>
    </ImageGalleryWrapper>
  )
}

export const ReviewCard: React.FC<IProps> = ({
  review,
  showReply,
  singleStretch,
  showImage = false,
}) => {
  return (
    <Review className={singleStretch ? 'singleStretch' : ''}>
      <div className="Review_Main">
        <div className="Review_Header">
          <Score score={Math.round(review.rating / 2)} />
          <span>{review.review_date}</span>
        </div>

        <div className="Review_Body">
          <div>
            <ReviewAvatar review={review} />
            <TextWrap text={review.body} />
          </div>

          {review.reviewable && review.reviewable_type === 'Post' && (
            <a
              href={`/posts/${review.reviewable.family_owner_slug || review.reviewable.slug}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Typography color="primary" type="subtitle" size="xl">
                {review.reviewable.name}
              </Typography>
            </a>
          )}
          {showImage && review.review_image_urls && review.review_image_urls.length > 0 && (
            <ReviewImages imageUrls={review.review_image_urls} reviewId={review.id} />
          )}
        </div>
      </div>
    </Review>
  )
}

const Avatar = styled.div`
  display: flex;
  align-items: center;
  flex: 1;

  .Avatar_Image {
    width: 3.6rem;
    height: 3.6rem;
    margin-right: 1.2rem;
  }

  .Avatar_Info {
    flex: 1;

    h4 {
      line-height: 2.4rem;
      font-size: 1.5rem;
      word-break: break-all;
      margin: 0;
    }
  }
`

const ImageGalleryWrapper = styled.div`
  position: relative;

  .ShowAll {
    position: absolute;
    top: -3.2rem;
    right: 0;
    margin: 0;
    z-index: 10;

    button {
      background: rgba(0, 0, 0, 0.7);
      color: white;
      border: none;
      padding: 0.4rem 0.8rem;
      border-radius: 0.4rem;
      font-size: 1.2rem;
      cursor: pointer;
      transition: background-color 0.2s ease;

      &:hover {
        background: rgba(0, 0, 0, 0.9);
      }
    }

    @media (max-width: ${constants.BREAKPOINT_TABLET}px) {
      top: -2.8rem;

      button {
        padding: 0.3rem 0.6rem;
        font-size: 1.1rem;
      }
    }
  }

  .reviewSwiper {
    .swiper-slide {
      width: auto;

      img {
        border-radius: 0.8rem;
        @media (max-width: ${constants.BREAKPOINT_TABLET}px) {
          border-radius: 0.6rem;
        }
      }
    }
  }

  .button-prev,
  .button-next {
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    top: 50%;
    width: 3rem;
    height: 3rem;
    background-color: rgba(0, 0, 0, 0.5);
    border: 0;
    border-radius: 50%;
    transform: translateY(-50%);
    transition: background-color 0.2s;
    z-index: 100;
    cursor: pointer;

    .icon {
      color: ${constants.COLORS.white};
      width: 1.8rem;
      height: 1.8rem;
    }

    &:hover {
      background-color: rgba(0, 0, 0, 0.75);
    }

    &.button-disabled {
      background-color: rgba(0, 0, 0, 0.25);
      .icon {
        color: ${constants.COLORS.disabled};
        opacity: 0.5;
        cursor: default;
      }
    }

    @media (max-width: ${constants.BREAKPOINT_TABLET}px) {
      width: 2.5rem;
      height: 2.5rem;

      .icon {
        width: 1.5rem;
        height: 1.5rem;
      }
    }
  }

  .button-prev {
    left: 0.8rem;
    @media (max-width: ${constants.BREAKPOINT_TABLET}px) {
      left: 0.6rem;
    }
  }

  .button-next {
    right: 0.8rem;
    @media (max-width: ${constants.BREAKPOINT_TABLET}px) {
      right: 0.6rem;
    }
  }
`

const ImageContainer = styled.div`
  position: relative;
  width: 120px;
  height: 120px;
  border-radius: 0.8rem;
  overflow: hidden;
  flex-shrink: 0;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: ${constants.COLORS.divider2 || '#f0f0f0'};

  &:hover {
    opacity: 0.8;
    transform: scale(1.02);
  }

  &:focus {
    outline: 2px solid ${constants.COLORS.primary || '#007bff'};
    outline-offset: 2px;
  }

  &:active {
    transform: scale(0.98);
  }

  @media (max-width: ${constants.BREAKPOINT_TABLET}px) {
    width: 100px;
    height: 100px;
    border-radius: 0.6rem;
  }

  @media (max-width: ${constants.BREAKPOINT_TABLET_MOBILE || 480}px) {
    width: 80px;
    height: 80px;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    display: block;
    transition: transform 0.2s ease;
  }

  &:hover img {
    transform: scale(1.05);
  }
`

const Review = styled.div`
  height: 100%;
  padding: 2rem;
  border: 1px solid ${constants.COLORS.divider2};
  border-radius: 1.6rem;
  background-color: white;

  &.singleStretch {
    height: min-content;
    flex: 1 1 auto;
  }

  @media (max-width: ${constants.BREAKPOINT_TABLET}px) {
    display: block;
    padding: 16px;
    width: 100%;
    flex: 1 1 auto;
  }

  .Review_Score {
    width: 106px;
    margin-top: 20px;
  }

  .Review_Details {
    width: 400px;
    display: flex;
    flex-wrap: wrap;
    margin-top: 10px;

    @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
      width: auto;
    }

    .Review_Score {
      flex-shrink: 0;
      margin-top: 10px;
      margin-right: 1em;
    }
  }

  .Review_Header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.2rem;

    & + a .subtitle {
      margin-top: 0.8rem;
    }

    span {
      font-size: 1.2rem;
      color: ${constants.COLORS.subText};
    }
  }

  .Review_Body {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-top: 0.8rem;
    line-height: 2rem;
    font-size: 1.4rem;
    white-space: pre-wrap;

    .Content {
      margin-top: 12px;
    }
  }
`
